import clsx from 'clsx'
import { type ComponentPropsWithRef, type ElementType, type JSX } from 'react'

type CardSize = 'zero' | 'small' | 'default' | 'big' | 'large'

type CardProps<T extends ElementType> = ComponentPropsWithRef<T> & {
    as?: T
    noBorder?: boolean
}

const Card = <T extends ElementType = 'div'>(props: CardProps<T>): JSX.Element => {
    const { children, className, as: Component = 'div', noBorder = false, ...otherProps } = props

    return (
        <Component
            className={clsx(
                { 'border shadow-xs': !noBorder },
                'overflow-hidden rounded-xl border-neutral-200 transition dark:border-neutral-700 dark:bg-neutral-800 dark:shadow-neutral-800',
                className,
            )}
            {...otherProps}
        >
            {children}
        </Component>
    )
}

Card.displayName = 'Card'

type CardAlignType = 'left' | 'center' | 'right' | 'space'

type CardSubPartProps<T extends ElementType = 'div'> = ComponentPropsWithRef<T> & {
    as?: T
    size?: CardSize
    align?: CardAlignType
    scrollHorizontally?: boolean
    noBorder?: boolean
}

const CardPart = <T extends ElementType = 'div'>(props: CardSubPartProps<T>): JSX.Element => {
    const { as = 'div', align, className, scrollHorizontally, ...otherProps } = props
    const Component = as

    return (
        <Component
            className={clsx(
                'flex-wrap',
                { 'flex justify-start gap-4': align === 'left' },
                { 'flex justify-end gap-4': align === 'right' },
                { 'flex justify-center gap-4': align === 'center' },
                { 'flex justify-between gap-4': align === 'space' },
                scrollHorizontally &&
                    'overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500 [&::-webkit-scrollbar-track]:bg-neutral-100 dark:[&::-webkit-scrollbar-track]:bg-neutral-700',
                className,
            )}
            {...otherProps}
        />
    )
}

CardPart.displayName = 'CardPart'

const HeaderPartSize = {
    zero: '',
    small: 'px-4 py-3',
    default: 'px-4 py-3 md:px-6 md:py-4 ',
    big: 'px-6 py-4 md:px-8 md:py-6',
    large: 'px-8 py-6 md:px-10 md:py-8',
}
const ContentPartSize = {
    zero: '',
    small: 'px-4 py-3',
    default: 'px-4 py-3 md:px-6 md:py-4 ',
    big: 'px-6 py-4 md:px-8 md:py-6',
    large: 'px-8 py-6 md:px-10 md:py-8',
}
const FooterPartSize = {
    zero: '',
    small: 'px-4 py-3',
    default: 'px-4 py-3 md:px-6 md:py-4 ',
    big: 'px-6 py-4 md:px-8 md:py-6',
    large: 'px-8 py-6 md:px-10 md:py-8',
}

const CardHeader = <T extends ElementType = 'header'>(props: CardSubPartProps<T>): JSX.Element => {
    const { className, size = 'default', as = 'header', noBorder, ...otherProps } = props
    const cardSize = size as CardSize
    return (
        <CardPart
            {...otherProps}
            as={as}
            className={clsx(
                { 'border-b': !noBorder },
                'border-neutral-200 dark:border-neutral-700',
                HeaderPartSize[cardSize],
                className,
            )}
        />
    )
}

CardHeader.displayName = 'CardHeader'

const CardContent = <T extends ElementType = 'div'>(props: CardSubPartProps<T>): JSX.Element => {
    const { className, size = 'default', as = 'div', ...otherProps } = props
    const cardSize = size as CardSize
    return <CardPart {...otherProps} as={as} className={clsx(className, ContentPartSize[cardSize])} />
}

CardContent.displayName = 'CardContent'

const CardFooter = <T extends ElementType = 'footer'>(props: CardSubPartProps<T>): JSX.Element => {
    const { className, size = 'default', as = 'footer', noBorder, ...otherProps } = props
    const cardSize = size as CardSize
    return (
        <CardPart
            {...otherProps}
            as={as}
            className={clsx(
                { 'border-t': !noBorder },
                'border-neutral-200 dark:border-neutral-700',
                FooterPartSize[cardSize],
                className,
            )}
        />
    )
}

CardFooter.displayName = 'CardFooter'

export { Card, CardContent, CardFooter, CardHeader }
